<template>
  <div class="details-wrapper">
    <div class="details-header">
      <div>Payroll Overview# {{ payrollOverviewDetails.reference_number }}</div>
      <div v-if="payrollOverviewDetails">
        Last modified by {{ payrollOverviewDetails.created_by_name }} at
        {{ formatDateWithTime(payrollOverviewDetails.created_at) }}
      </div>
    </div>
    <div class="details-body">
      <div class="content-panel-wrapper">
        <section class="details-section">
          <div class="payroll-overview-details">
            <div class="parameters-section">
              <div>
                <label>{{ $t('payrollOverviews.details.date_range') }} </label>
                {{ formatDate(payrollOverviewDetails.range_start) }} to
                {{ formatDate(payrollOverviewDetails.range_end) }}
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.departments') }} </label>
                {{ payrollOverviewDetails.departments }}
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.pay_period') }} </label>
                {{ payrollOverviewDetails.pay_period_type }}
              </div>
            </div>
            <div v-if="generatedDetails" class="generated-details-section">
              <label>{{ $t('payrollOverviews.details.tol_generated') }} </label>
              <div>
                <label>{{ $t('payrollOverviews.details.date') }} </label>
                {{ formatDateWithTime(generatedDetails.created_at) }}
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.user') }} </label>
                {{ generatedDetails.created_by_name }}
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.timesheets') }} </label>
                {{ payrollOverviewDetails.total_time_sheets }}
              </div>
              <div>
                <button @click="downloadExportFile()">Download Export File</button>
              </div>
            </div>
            <div v-if="resultDetails" class="results-section">
              <label>{{ $t('payrollOverviews.details.results') }} </label>
              <div>
                <label>{{ $t('payrollOverviews.details.date') }} </label>
                {{ formatDateWithTime(resultDetails.processed_date) }}
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.user') }} </label>
                {{ resultDetails.created_by_name }}
              </div>
              <div class="results-processed">
                <div>
                  <label>{{ $t('payrollOverviews.details.processed') }} </label>
                  {{ payrollOverviewDetails.num_processed_automatically }}
                </div>
              </div>
              <div>
                <label>{{ $t('payrollOverviews.details.received') }} </label>
                {{ resultDetails.timeSheetsInReceivedStatus }}
              </div>
            </div>
            <div v-else class="results-section">
              <label>{{ $t('payrollOverviews.details.ptb_processed') }} </label>
              <div>
                <button @click="uploadResults()">
                  {{ $t('payrollOverviews.details.upload_results') }}
                </button>
              </div>
              <div class="results-file-name">
                {{
                  `IMPORT_RESULTS_PAYROLL_OVERVIEW_${payrollOverviewDetails.range_end}_${
                    payrollOverviewDetails.reference_number
                  }.CSV`
                }}
              </div>
            </div>
          </div>
        </section>
        <!--time sheets -->
        <section class="details-section">
          <h1>{{ $t('timeSheets.listing.title') }}</h1>
          <tol-table
            :table-data="payrollOverviewTimesheets"
            :columns="timeSheetsColumns"
            data-label-prefix="timeSheets"
            :show-spinner="isTimeSheetLoading"
          >
            <template v-slot:tableHeader>
              <th-sortable
                :direction="
                  currentSortFieldForTimeSheets === 'payrollNumber'
                    ? currentSortDirectionForTimeSheets
                    : 'none'
                "
                field-name="payrollNumber"
                @change="onTimeSheetsSortOrderChange"
                >{{ $t('timeSheets.listing.labels.payrollNumber') }}</th-sortable
              >
              <th>{{ $t('timeSheets.listing.labels.contractor') }}</th>
              <th-sortable
                :direction="
                  currentSortFieldForTimeSheets === 'startDate'
                    ? currentSortDirectionForTimeSheets
                    : 'none'
                "
                field-name="startDate"
                @change="onTimeSheetsSortOrderChange"
                >{{ $t('timeSheets.listing.labels.startDate') }}</th-sortable
              >

              <th-sortable
                :direction="
                  currentSortFieldForTimeSheets === 'endDate'
                    ? currentSortDirectionForTimeSheets
                    : 'none'
                "
                field-name="endDate"
                @change="onTimeSheetsSortOrderChange"
                >{{ $t('timeSheets.listing.labels.endDate') }}</th-sortable
              >

              <th-sortable
                :direction="
                  currentSortFieldForTimeSheets === 'totalHours'
                    ? currentSortDirectionForTimeSheets
                    : 'none'
                "
                field-name="totalHours"
                @change="onTimeSheetsSortOrderChange"
                >{{ $t('timeSheets.listing.labels.totalHours') }}</th-sortable
              >

              <th-sortable
                :direction="
                  currentSortFieldForTimeSheets === 'totalDays'
                    ? currentSortDirectionForTimeSheets
                    : 'none'
                "
                field-name="totalDays"
                @change="onTimeSheetsSortOrderChange"
                >{{ $t('timeSheets.listing.labels.totalDays') }}</th-sortable
              >
              <th>{{ $t('timeSheets.listing.labels.status') }}</th>
            </template>

            <template v-slot:referenceNumber="{ value }">
              <router-link :to="'/time-sheets/' + value.referenceNumber">
                {{ value.referenceNumber }}
              </router-link>
            </template>

            <template v-slot:status="{ value }">
              <Status :status="value.status" object-type="time-sheet" />
            </template>
          </tol-table>
          <Pagination
            :records-per-page="timeSheetsPerPage"
            :offset="currentPage"
            :record-count="recordCount"
            @change="onChangePaginationParams($event)"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {
  PayrollOverviewDetailsGetters,
  PayrollOverviewDetailsActions
} from './payroll-overview-details.keys';
import { AppActions } from '@/app/app.keys';
import format from 'date-fns/format';
import TolTable from '@/app/components/TolTable';
import Status from '@/app/components/Status';
import ThSortable from '@/app/components/ThSortable';
import Pagination from '@/app/components/Pagination';
import { attachmentService } from '../../../../components/attachments/attachment.service';
export default {
  name: 'PayrollOverviewDetails',
  components: { TolTable, Status, ThSortable, Pagination },
  data() {
    return {
      timeSheetsColumns: [
        'payrollNumber',
        'contractorName',
        'startDate',
        'endDate',
        'totalHours',
        'totalDays',
        'status'
      ]
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOverviewDetailsIsLoading];
    },
    isTimeSheetLoading() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOverviewTimesheetsIsLoading];
    },
    timeSheetsPerPage() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOveriewTimeSheetsPerPage];
    },
    recordCount() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOverviewTimesheetsCount];
    },
    currentPage() {
      return this.$store.getters[
        PayrollOverviewDetailsGetters.PayrollOveriewTimeSheetsListingOffset
      ];
    },
    payrollOverviewDetails() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOverviewDetails];
    },
    payrollOverviewTimesheets() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOverviewTimesheets];
    },
    currentSortFieldForTimeSheets() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOveriewTimeSheetsSortOrder]
        .sortBy;
    },
    currentSortDirectionForTimeSheets() {
      return this.$store.getters[PayrollOverviewDetailsGetters.PayrollOveriewTimeSheetsSortOrder]
        .sortDirection;
    },
    generatedDetails() {
      return this.payrollOverviewDetails.attachments
        ? this.payrollOverviewDetails.attachments.filter(attachment => {
            if (attachment.category == 'Export') return attachment;
          })[0]
        : null;
    },
    resultDetails() {
      return this.payrollOverviewDetails.resultDetails;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(val) {
        this.$store.dispatch(
          PayrollOverviewDetailsActions.RetrievepayrollOverviewDetails,
          val.params.id
        );
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('payrollOverviews.details.title'));
  },
  methods: {
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },
    formatDateWithTime(d) {
      return d ? format(d, 'YYYY-MM-DD HH:mm') : null;
    },
    onTimeSheetsSortOrderChange(sortOrder) {
      this.$store.dispatch(
        PayrollOverviewDetailsActions.SetPayrollOverviewTimeSheetsSortOrder,
        sortOrder
      );
    },
    onChangePaginationParams(event) {
      this.$store.dispatch(
        PayrollOverviewDetailsActions.SetPayrollOverviewTimeSheetsPaginationParameters,
        event
      );
    },
    uploadResults() {
      this.$store.dispatch(
        PayrollOverviewDetailsActions.ProcessPayrollOverviewResults,
        `IMPORT_RESULTS_PAYROLL_OVERVIEW_${this.payrollOverviewDetails.range_end}_${
          this.payrollOverviewDetails.reference_number
        }.CSV`
      );
    },
    downloadExportFile() {
      attachmentService.download(
        this.payrollOverviewDetails.attachments[0].id,
        this.payrollOverviewDetails.attachments[0].original_name
      );
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
.details-wrapper {
  display: flex;
  flex-direction: column;
  padding: 5%;
  background-color: $background-level-2;
  .details-header {
    display: flex;
    align-items: center;
    padding: $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
  }

  .details-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }
    }
  }
}

.payroll-overview-details {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 5%;
  label {
    font-weight: bold;
  }
}

.generated-details-section {
  border: 1px solid $light-grey-1;
  padding: 1%;
}

.results-section {
  border: 1px solid $light-grey-1;
  padding: 1%;
}

.results-processed {
  position: relative;
}

.tooltip {
  background-color: $black;
  color: $white;
  padding: 5px 3px;
  font-size: 0.75em;
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translate(-50%, 0);
  white-space: nowrap;
}

.confirm-btn {
  width: 10rem;
  opacity: 1;
  position: absolute;
  padding: 0.8rem 3rem;
  color: $white;
  pointer-events: auto;
  &:hover {
    cursor: pointer;
  }
}

.results-file-name {
  width: 50%;
  word-wrap: break-word;
}
</style>
